import { useState, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Dices, TrendingUp, TrendingDown, History } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const DicePage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [target, setTarget] = useState(50);
  const [isOver, setIsOver] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  const multiplier = isOver ? (100 / (100 - target)).toFixed(2) : (100 / target).toFixed(2);
  const winChance = isOver ? (100 - target) : target;

  const playDice = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/games/dice/play`, {
        game_type: "dice",
        bet_amount: betAmount,
        bet_data: { target, over: isOver }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLastResult(response.data);
      setHistory(prev => [response.data, ...prev.slice(0, 9)]);
      
      if (response.data.won) {
        toast.success(`Won ₹${response.data.winnings.toFixed(2)}! Rolled ${response.data.roll}`);
      } else {
        toast.error(`Lost! Rolled ${response.data.roll}`);
      }
      
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#007AFF]/20 flex items-center justify-center">
            <Dices className="w-6 h-6 text-[#007AFF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="dice-title">Dice</h1>
            <p className="text-[#A1A1AA] text-sm">Roll over or under your target number</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] p-6">
              {/* Result Display */}
              <div className="mb-8 text-center">
                {lastResult ? (
                  <div className={`inline-flex flex-col items-center p-8 rounded-2xl ${
                    lastResult.won ? 'bg-[#00E701]/10' : 'bg-[#FF3B30]/10'
                  }`}>
                    <span className="text-[#A1A1AA] text-sm mb-2">Roll Result</span>
                    <span className={`text-7xl font-bold font-mono ${
                      lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'
                    }`} data-testid="dice-result">
                      {lastResult.roll}
                    </span>
                    <span className={`mt-2 text-lg font-bold ${
                      lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'
                    }`}>
                      {lastResult.won ? `+₹${lastResult.winnings.toFixed(2)}` : 'LOST'}
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex flex-col items-center p-8 rounded-2xl bg-white/5">
                    <span className="text-[#A1A1AA] text-sm mb-2">Roll Result</span>
                    <span className="text-7xl font-bold font-mono text-[#52525B]">?</span>
                  </div>
                )}
              </div>

              {/* Target Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[#A1A1AA]">Target Number</span>
                  <span className="text-2xl font-bold font-mono text-white">{target}</span>
                </div>
                
                <div className="relative">
                  <div className="h-4 rounded-full bg-gradient-to-r from-[#FF3B30] via-[#FFD600] to-[#00E701] mb-2" />
                  <Slider
                    value={[target]}
                    onValueChange={(v) => setTarget(v[0])}
                    min={5}
                    max={95}
                    step={1}
                    className="w-full"
                    data-testid="dice-target-slider"
                  />
                  <div className="flex justify-between text-[#52525B] text-sm mt-2">
                    <span>5</span>
                    <span>50</span>
                    <span>95</span>
                  </div>
                </div>
              </div>

              {/* Over/Under Toggle */}
              <div className="flex gap-4 mb-6">
                <Button
                  onClick={() => setIsOver(false)}
                  variant={!isOver ? "default" : "outline"}
                  className={`flex-1 h-14 ${
                    !isOver 
                      ? 'bg-[#FF3B30] hover:bg-[#E63329] text-white' 
                      : 'border-white/10 text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid="dice-under-btn"
                >
                  <TrendingDown className="w-5 h-5 mr-2" />
                  Roll Under {target}
                </Button>
                <Button
                  onClick={() => setIsOver(true)}
                  variant={isOver ? "default" : "outline"}
                  className={`flex-1 h-14 ${
                    isOver 
                      ? 'bg-[#00E701] hover:bg-[#00C201] text-black' 
                      : 'border-white/10 text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid="dice-over-btn"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Roll Over {target}
                </Button>
              </div>

              {/* Bet Amount */}
              <div className="mb-6">
                <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                <div className="flex gap-4">
                  <Input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(Number(e.target.value))}
                    className="flex-1 h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                    data-testid="dice-bet-input"
                  />
                  <div className="flex gap-2">
                    {[50, 100, 500, 1000].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <span className="text-[#A1A1AA] text-sm block">Multiplier</span>
                  <span className="text-xl font-bold text-white font-mono">{multiplier}x</span>
                </div>
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <span className="text-[#A1A1AA] text-sm block">Win Chance</span>
                  <span className="text-xl font-bold text-white font-mono">{winChance}%</span>
                </div>
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <span className="text-[#A1A1AA] text-sm block">Potential Win</span>
                  <span className="text-xl font-bold text-[#00E701] font-mono">
                    ₹{(betAmount * Number(multiplier)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Roll Button */}
              <Button
                onClick={playDice}
                disabled={loading || betAmount <= 0 || betAmount > (user?.balance || 0)}
                className={`w-full h-14 font-bold text-lg ${
                  isOver 
                    ? 'bg-[#00E701] hover:bg-[#00C201] text-black btn-glow' 
                    : 'bg-[#FF3B30] hover:bg-[#E63329] text-white'
                }`}
                data-testid="dice-roll-btn"
              >
                {loading ? 'Rolling...' : `ROLL ${isOver ? 'OVER' : 'UNDER'} ${target}`}
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">Roll History</h3>
              </div>
              
              <div className="space-y-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      h.won ? 'bg-[#00E701]/10' : 'bg-[#FF3B30]/10'
                    }`}
                  >
                    <span className={`font-mono font-bold ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.roll}
                    </span>
                    <span className="text-[#A1A1AA] text-sm">
                      {h.over ? '>' : '<'} {h.target}
                    </span>
                    <span className={`font-mono text-sm ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.won ? `+₹${h.winnings?.toFixed(0)}` : '-'}
                    </span>
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm text-center py-4">No rolls yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
